import {
  Body,
  IconBadge,
  IconBadgeUse,
  PhotoAvatar,
  Skeleton,
  Subhead,
} from "@growtherapy/sprout-ui";
import { Provider } from "../../../types";
import { useGetTelehealthSessionInfo } from "../../../hooks";
import { useParams } from "react-router-dom";
import { getLocalizedPronoun } from "../../../utils";
import { formatAppointmentTime } from "../utils";
import { faUser } from "@fortawesome/pro-solid-svg-icons";

import { useTranslate } from "@growtherapy/i18n";

export type NewProviderAvatarProps = {
  appointmentStartTime?: string;
  appointmentEndTime?: string;
  provider?: Pick<Provider, "image" | "name" | "pronouns">;
  loading?: boolean;
};

/**
 * Redesigned provider avatar and info for the intro room. This UI does NOT
 * support the pre-rebrand design system.
 */
export function NewProviderAvatar({
  appointmentStartTime,
  appointmentEndTime,
  provider,
  loading,
}: NewProviderAvatarProps) {
  const t = useTranslate();
  return (
    <div className="flex flex-row gap-4 justify-center sm:justify-start items-center w-full max-sm:px-350">
      {loading || !provider ? (
        <>
          <Skeleton className="my-0 mx-0 rebrand:rounded-200-250 rebrand:size-component-250" />
          <div className="flex flex-col gap-3 mt-1">
            <Skeleton className="my-0 mx-0 h-4 w-40" />
            <Skeleton className="my-0 mx-0 w-44" />
          </div>
        </>
      ) : (
        <>
          {provider.image ? (
            <PhotoAvatar
              alt={
                provider?.name
                  ? t("telehealth-core:providerInfo.providerName_dynamic", {
                      providerName: provider.name,
                    })
                  : t("telehealth-core:providerInfo.providerPicture")
              }
              src={provider.image ?? ""}
              data-dd-privacy="mask"
              size="md"
              imgClassName="fs-exclude rebrand:rounded-200-250"
            />
          ) : (
            <IconBadge
              icon={faUser}
              use={IconBadgeUse.Neutral}
              className="rebrand:rounded-200-250 rebrand:size-component-250"
              iconClassName="rebrand:size-component-100"
            />
          )}
          <div className="flex flex-col-reverse gap-1">
            <Subhead.Small
              as="h2"
              className="fs-exclude flex flex-wrap gap-x-2 gap-y-1"
              data-dd-privacy="mask"
            >
              <span>
                {provider.name ?? t("telehealth-core:introRoom.yourProvider")}
              </span>
              {!!provider.pronouns?.length && (
                <span className="rebrand:text-text-muted font-normal">
                  ({getLocalizedPronoun(t, provider.pronouns[0])})
                </span>
              )}
            </Subhead.Small>
            <Body.Small>
              {formatAppointmentTime(
                t,
                appointmentStartTime,
                appointmentEndTime,
              ) || t("telehealth-core:introRoom.sessionWith")}
            </Body.Small>
          </div>
        </>
      )}
    </div>
  );
}

export function NewProviderAvatarWrapper() {
  const { providerShortId, patientShortId } = useParams();
  const { data, loading } = useGetTelehealthSessionInfo(
    providerShortId,
    patientShortId,
  );
  const provider = data?.telehealthSessionInfo?.provider;
  const appointmentStartTime =
    data?.telehealthSessionInfo?.appointment?.timeStart;
  const appointmentEndTime = data?.telehealthSessionInfo?.appointment?.timeEnd;
  return (
    <NewProviderAvatar
      appointmentStartTime={appointmentStartTime}
      appointmentEndTime={appointmentEndTime}
      provider={provider}
      loading={loading}
    />
  );
}
